'use client';

import { useLayoutEffect } from 'react';
import { scroller } from 'react-scroll';

import { COMMENT_SORT_TYPE, COMMENT_TYPE } from '@/api';
import { SELECTOR } from '@/constants';
import { useToastPoint } from '@/hooks/useToastPoint';
import {
  CONTENT_CATEGORY,
  GTM_EVENT_TYPE,
  trackGtmEvent,
} from '@/utils/analytics/gtm';

const COMMENT_HASH_SCROLL_TIMEOUT_MS = 1_500;

const COMMENT_CONTENT_CATEGORY = {
  [COMMENT_TYPE.POLICY]: CONTENT_CATEGORY.POLICY,
  [COMMENT_TYPE.POLICY_INFO]: CONTENT_CATEGORY.POLICY_NEWS,
  [COMMENT_TYPE.HOMETOWN_NEWS]: CONTENT_CATEGORY.HOMETOWN_NEWS,
  [COMMENT_TYPE.COMMUNITY]: CONTENT_CATEGORY.COMMUNITY,
} as const;

// window 스크롤 기준으로 댓글 리스트 시작 위치를 다시 계산한다.
export const scrollToCommentListTop = (behavior: ScrollBehavior = 'auto') => {
  const commentListTopElement = document.getElementById(
    SELECTOR.COMMENT_LIST_TOP,
  );

  if (!commentListTopElement) return;

  const targetRect = commentListTopElement.getBoundingClientRect();
  const nextTop = window.scrollY + targetRect.top - 100;

  window.scrollTo({
    top: Math.max(nextTop, 0),
    behavior,
  });
};

// 서버 반영 후 새 댓글/수정 댓글 위치로 자연스럽게 이동시키는 공통 함수다.
export const scrollToCommentTarget = (commentId: number) => {
  setTimeout(() => {
    scroller.scrollTo(SELECTOR.COMMENT_PREFIX + commentId, {
      duration: 300,
      smooth: 'easeOutQuad',
      offset: -100,
    });
  }, 300);
};

// URL hash 기반 진입 시 이미지 로딩 등으로 위치가 흔들려도 잠시 동안 다시 맞춰준다.
export const useCommentThreadHashScroll = (pathname: string) => {
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== `#${SELECTOR.COMMENT_LIST_TOP}`) return;

    const startTime = performance.now();
    let frameId: number | null = null;

    scrollToCommentListTop();

    const keepCommentListAligned = () => {
      if (performance.now() - startTime >= COMMENT_HASH_SCROLL_TIMEOUT_MS) {
        return;
      }

      scrollToCommentListTop();
      frameId = window.requestAnimationFrame(keepCommentListAligned);
    };

    frameId = window.requestAnimationFrame(keepCommentListAligned);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [pathname]);
};

interface UseCommentPostSuccessParams {
  /** GTM 카테고리 분기와 정렬 reset 판단에 사용 */
  commentType: COMMENT_TYPE;
  /** GTM 전송용 원문 id */
  contentId: number;
  /** GTM용 댓글 대상 제목 */
  title?: string;
  /** 정책 관련 화면의 GTM용 정책명 */
  policyName?: string;
  /** 현재 목록 정렬 상태 */
  sortType: COMMENT_SORT_TYPE;
  /** 작성 직후 최신순으로 되돌리는 함수 */
  resetSortType: () => void;
}

// 댓글 작성 성공 시 공통 후처리(GTM, 정렬 복원, 포인트 토스트, 스크롤)를 묶는다.
export const useCommentPostSuccess = ({
  commentType,
  contentId,
  title,
  policyName,
  sortType,
  resetSortType,
}: UseCommentPostSuccessParams) => {
  const { toastPoint } = useToastPoint();

  return (commentId: number, point?: number) => {
    trackGtmEvent(GTM_EVENT_TYPE.POST_COMMENT, {
      content_name: policyName,
      comment_name: title,
      content_category: COMMENT_CONTENT_CATEGORY[commentType],
      content_id: contentId.toString(),
    });

    if (sortType !== COMMENT_SORT_TYPE.LATEST) {
      resetSortType();
    }

    scrollToCommentListTop('smooth');

    if (point) {
      toastPoint(point);
    }

    scrollToCommentTarget(commentId);
  };
};
